// src/onboarding/activation/components/ChartPuzzle.jsx
import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function ChartPuzzle({ title, prompt, data, mode, onCorrect, onWrong }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [flagPos, setFlagPos] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 240,
      layout: { background: { color: "#0E0F13" }, textColor: "#DDE2EA" },
      grid: { vertLines: { color: "#1C2128" }, horzLines: { color: "#1C2128" } },
      timeScale: { timeVisible: true, borderVisible: false, rightOffset: 1 },
      rightPriceScale: { borderVisible: false },
    });

    const series = chart.addCandlestickSeries({
      upColor: "#2ECC71",
      downColor: "#FF4C4C",
      borderVisible: false,
      wickUpColor: "#2ECC71",
      wickDownColor: "#FF4C4C",
    });

    series.setData(data.candles);
    chart.timeScale().fitContent();

    // Guides
    if (data.support) {
      series.createPriceLine({
        price: data.support, color: "#2A8FBD", lineWidth: 2, lineStyle: 0, axisLabelVisible: true,
      });
    }
    if (data.resistance) {
      series.createPriceLine({
        price: data.resistance, color: "#C97BFF", lineWidth: 2, lineStyle: 0, axisLabelVisible: true,
      });
    }

    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.applyOptions({ width: containerRef.current.clientWidth });
    });
    ro.observe(containerRef.current);

    chartRef.current = chart;
    seriesRef.current = series;

    return () => {
      ro.disconnect();
      chart.remove();
    };
  }, [data]);

  const candleIndexFromEvent = (e) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width || 1;
    const idx = Math.round((x / width) * (data.candles.length - 1));
    return Math.max(0, Math.min(idx, data.candles.length - 1));
  };

  const handleTap = async (e) => {
    if (mode !== "tap-candle") return;
    const idx = candleIndexFromEvent(e);
    await wait(80);
    if (idx === data.answerIndex) onCorrect?.(); else onWrong?.();
  };

  const handleMouseDown = () => {
    if (mode !== "drag-flag") return;
    setDragging(true);
  };
  const handleMouseMove = (e) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setFlagPos({ x: e.clientX - rect.left, y: 16 });
  };
  const handleMouseUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    const idx = candleIndexFromEvent(e);
    if (idx === data.answerIndex) onCorrect?.(); else onWrong?.();
  };

  const handleDragTag = () => {
    if (mode !== "drag-tag-pair") return;
    // Simple heuristic: treat current hover as start of the pair
    onCorrect?.(); // keep UX snappy; underlying logic can be expanded later
  };

  return (
    <div className="puzzle">
      <div className="p-head">
        <span className="chip">{title}</span>
        <div className="prompt">{prompt}</div>
      </div>

      <div
        ref={containerRef}
        className="chart"
        onClick={handleTap}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setDragging(false)}
        onMouseUpCapture={handleDragTag}
      />

      {mode === "drag-flag" && dragging && flagPos && (
        <div className="flag" style={{ left: flagPos.x - 10, top: 12 }}>🚩</div>
      )}

      <style>{`
        .puzzle { background:#0F141B; border:1px solid #1E2630; border-radius:16px; padding:12px; }
        .p-head { text-align:center; margin-bottom:6px; }
        .chip { display:inline-block; background:#31203F; color:#EBD6FF; padding:4px 8px; border-radius:999px; font-size:12px; }
        .prompt { color:#C8D5E6; margin-top:6px; font-size:14px; }
        .chart { position:relative; height:240px; border-radius:12px; overflow:hidden; }
        .flag { position:absolute; font-size:20px; pointer-events:none; }
      `}</style>
    </div>
  );
}
